import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { 
    Grid, Typography, Box, Paper, CircularProgress, Alert, 
    FormControl, InputLabel, Select, MenuItem, Divider, Chip
} from '@mui/material';
import { 
    TrendingUp, People, Search, Psychology, Category, 
    Share, PresentToAll, Lightbulb, Timeline, CheckCircle,
    ErrorOutline, Description
} from '@mui/icons-material';
import { useTheme } from '../utils/ThemeContext';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import '../assets/css/futuristic-modern.css';

/**
 * Analytics Dashboard Component
 * 
 * Displays comprehensive analytics metrics for monitoring user adoption
 * Shows DAU/WAU/MAU, feature usage, funnel metrics, and retention
 * 
 * **Admin-Only Access** - Requires is_admin flag in user database
 */
function AnalyticsDashboard() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState(30);
    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    const COLORS = ['#6c3082', '#468230', '#7986cb', '#81E6D9', '#8e44ad'];

    useEffect(() => {
        checkAdminStatus();
    }, []);

    useEffect(() => {
        if (isAdmin) {
            fetchMetrics();
        }
    }, [timeRange, isAdmin]);

    const checkAdminStatus = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                navigate('/signin');
                return;
            }

            const userProfileData = localStorage.getItem('userProfileData');
            if (!userProfileData) {
                setError('Please sign in again');
                setLoading(false);
                navigate('/signin');
                return;
            }

            const profile = JSON.parse(userProfileData);
            const isAdminUser = profile?.is_admin === true;

            if (isAdminUser) {
                setIsAdmin(true);
            } else {
                setError('Access Denied: Admin privileges required');
                setLoading(false);
            }
        } catch (err) {
            setError(`Failed to verify admin status: ${err.message}`);
            setLoading(false);
        }
    };

    const fetchMetrics = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(
                `${API_BASE_URL}/api/analytics/dashboard?days=${timeRange}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setMetrics(response.data.metrics);
            } else {
                throw new Error('Failed to fetch metrics');
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (num === undefined || num === null) return '0';
        return num.toLocaleString();
    };

    const formatPercentage = (num) => {
        if (num === undefined || num === null) return '0.0%';
        return `${num.toFixed(1)}%`;
    };

    // Custom tooltip for charts
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper 
                    elevation={3}
                    sx={{
                        p: 2,
                        backgroundColor: isDarkMode ? 'var(--surface-2)' : '#fff',
                        border: `1px solid ${isDarkMode ? 'var(--glass-border)' : '#e0e0e0'}`,
                        borderRadius: 'var(--radius-md)',
                    }}
                >
                    <Typography variant="body2" fontWeight="bold">{label}</Typography>
                    {payload.map((entry, index) => (
                        <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                            {entry.name}: {entry.value}
                        </Typography>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    // Metric Card Component
    const MetricCard = ({ icon, title, value, subtitle, color = '#6c3082', trend }) => (
        <Paper 
            elevation={0}
            className="dashboard-card"
            sx={{
                p: 3,
                height: '100%',
                background: 'transparent',
                border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                borderRadius: 'var(--radius-lg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 'var(--card-shadow-hover)',
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                    sx={{ 
                        p: 1, 
                        borderRadius: 'var(--radius-md)',
                        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                        display: 'flex',
                        mr: 2
                    }}
                >
                    {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 22 } })}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: isDarkMode ? 'var(--text-secondary)' : '#666',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            color: isDarkMode ? 'var(--text-primary)' : '#2c3e50',
                            fontWeight: 700,
                            lineHeight: 1
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
            </Box>
            {subtitle && (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: isDarkMode ? 'var(--text-tertiary)' : '#888',
                        fontSize: '0.85rem'
                    }}
                >
                    {subtitle}
                </Typography>
            )}
            {trend && (
                <Chip 
                    label={trend} 
                    size="small" 
                    sx={{ 
                        mt: 1,
                        backgroundColor: color + '20',
                        color: color,
                        fontWeight: 600
                    }} 
                />
            )}
        </Paper>
    );

    if (loading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    gap: 2
                }}
            >
                <CircularProgress size={60} sx={{ color: '#6c3082' }} />
                <Typography variant="h6" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#333' }}>
                    Loading analytics...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert 
                    severity={error.includes('Access Denied') ? 'error' : 'warning'}
                    sx={{ 
                        borderRadius: 'var(--radius-lg)',
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        {error.includes('Access Denied') ? '🔒 Access Denied' : 'Error Loading Analytics'}
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                    {error.includes('Access Denied') && (
                        <Box sx={{ mt: 2 }}>
                            <button 
                                className="btn btn-modern-primary"
                                onClick={() => navigate('/dashboard')}
                            >
                                Return to Dashboard
                            </button>
                        </Box>
                    )}
                </Alert>
            </Box>
        );
    }

    if (!metrics) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="info" sx={{ borderRadius: 'var(--radius-lg)' }}>
                    No analytics data available yet
                </Alert>
            </Box>
        );
    }

    // Prepare chart data
    const activeUsersData = [
        { name: 'DAU', value: metrics.active_users?.dau || 0, color: COLORS[0] },
        { name: 'WAU', value: metrics.active_users?.wau || 0, color: COLORS[1] },
        { name: 'MAU', value: metrics.active_users?.mau || 0, color: COLORS[2] }
    ];

    const featureUsageData = Object.entries(metrics.feature_usage || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([feature, count]) => ({
            name: feature.replace(/_/g, ' '),
            count: count
        }));

    const funnelData = [
        { stage: 'Signups', value: metrics.funnel_metrics?.funnel?.signup || 0 },
        { stage: 'Email Verified', value: metrics.funnel_metrics?.funnel?.email_verified || 0 },
        { stage: 'Profile Complete', value: metrics.funnel_metrics?.funnel?.profile_completed || 0 },
        { stage: 'First Search', value: metrics.funnel_metrics?.funnel?.first_search || 0 },
        { stage: 'Article Saved', value: metrics.funnel_metrics?.funnel?.first_article_saved || 0 },
        { stage: 'AI Used', value: metrics.funnel_metrics?.funnel?.ai_assistant_used || 0 }
    ];

    return (
        <Box sx={{ p: 3, minHeight: '100vh', maxHeight: '100vh', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography 
                        variant="h3" 
                        sx={{ 
                            color: isDarkMode ? 'var(--text-primary)' : '#2c3e50',
                            fontWeight: 700,
                            mb: 1,
                            background: 'linear-gradient(135deg, #6c3082 0%, #468230 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        📊 Analytics Dashboard
                    </Typography>
                    <Typography 
                        variant="body1" 
                        sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}
                    >
                        Monitor user adoption and engagement metrics
                    </Typography>
                </Box>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={timeRange}
                        label="Time Range"
                        onChange={(e) => setTimeRange(e.target.value)}
                        sx={{
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: isDarkMode ? 'var(--surface-2)' : '#fff',
                        }}
                    >
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Active Users Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        icon={<People />}
                        title="Daily Active Users"
                        value={formatNumber(metrics.active_users?.dau)}
                        subtitle="Users active in last 24 hours"
                        color="#6c3082"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        icon={<TrendingUp />}
                        title="Weekly Active Users"
                        value={formatNumber(metrics.active_users?.wau)}
                        subtitle="Users active in last 7 days"
                        color="#468230"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        icon={<Timeline />}
                        title="Monthly Active Users"
                        value={formatNumber(metrics.active_users?.mau)}
                        subtitle="Users active in last 30 days"
                        color="#7986cb"
                    />
                </Grid>
            </Grid>

            {/* Today's Activity */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Paper 
                        elevation={0}
                        className="dashboard-card"
                        sx={{
                            p: 3,
                            background: 'transparent',
                            border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                            borderRadius: 'var(--radius-lg)',
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 3, color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 600 }}>
                            Today's Activity
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <People sx={{ fontSize: 32, color: '#6c3082', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.today_summary?.active_users)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Active Users
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <CheckCircle sx={{ fontSize: 32, color: '#468230', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.today_summary?.new_signups)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        New Signups
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Search sx={{ fontSize: 32, color: '#7986cb', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.today_summary?.feature_usage?.searches)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Searches
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Psychology sx={{ fontSize: 32, color: '#81E6D9', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.today_summary?.feature_usage?.ai_queries)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        AI Queries
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Product Usage */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Paper 
                        elevation={0}
                        className="dashboard-card"
                        sx={{
                            p: 3,
                            background: 'transparent',
                            border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                            borderRadius: 'var(--radius-lg)',
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 3, color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 600 }}>
                            📊 Product Usage (All Time)
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Search sx={{ fontSize: 32, color: '#6c3082', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.queries?.total)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Total Queries
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? 'var(--text-tertiary)' : '#888' }}>
                                        {formatNumber(metrics.product_usage?.queries?.with_articles)} with articles
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Description sx={{ fontSize: 32, color: '#468230', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.articles?.total)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Articles Fetched
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? 'var(--text-tertiary)' : '#888' }}>
                                        Avg: {metrics.product_usage?.articles?.avg_per_query?.toFixed(1)} per query
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Category sx={{ fontSize: 32, color: '#7986cb', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.categories?.total)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Categories
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? 'var(--text-tertiary)' : '#888' }}>
                                        User organization
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Share sx={{ fontSize: 32, color: '#81E6D9', mb: 1 }} />
                                    <Typography variant="h4" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.collaboration?.total_messages)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Collaboration
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? 'var(--text-tertiary)' : '#888' }}>
                                        Team messages
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider sx={{ my: 3, borderColor: isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)' }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Psychology sx={{ fontSize: 30, color: '#6c3082', mb: 1 }} />
                                    <Typography variant="h5" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.ai_features?.conversations)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        AI Conversations
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Lightbulb sx={{ fontSize: 30, color: '#468230', mb: 1 }} />
                                    <Typography variant="h5" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.ai_features?.insights)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Insights Generated
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PresentToAll sx={{ fontSize: 30, color: '#7986cb', mb: 1 }} />
                                    <Typography variant="h5" sx={{ color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 700 }}>
                                        {formatNumber(metrics.product_usage?.ai_features?.presentations)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                        Presentations
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* User Journey Funnel */}
                <Grid item xs={12} md={6}>
                    <Paper 
                        elevation={0}
                        className="dashboard-card"
                        sx={{
                            p: 3,
                            height: '100%',
                            background: 'transparent',
                            border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                            borderRadius: 'var(--radius-lg)',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 600 }}>
                            User Journey Funnel
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={funnelData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e0e0e0'} />
                                <XAxis type="number" stroke={isDarkMode ? 'var(--text-secondary)' : '#666'} />
                                <YAxis dataKey="stage" type="category" stroke={isDarkMode ? 'var(--text-secondary)' : '#666'} width={120} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" fill="#6c3082" radius={[0, 8, 8, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ color: isDarkMode ? 'var(--text-secondary)' : '#666' }}>
                                <strong>Overall Activation Rate:</strong> {formatPercentage(metrics.funnel_metrics?.conversion_rates?.activated_users)}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Feature Usage */}
                <Grid item xs={12} md={6}>
                    <Paper 
                        elevation={0}
                        className="dashboard-card"
                        sx={{
                            p: 3,
                            height: '100%',
                            background: 'transparent',
                            border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                            borderRadius: 'var(--radius-lg)',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 600 }}>
                            Top Features ({timeRange} days)
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={featureUsageData}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e0e0e0'} />
                                <XAxis dataKey="name" stroke={isDarkMode ? 'var(--text-secondary)' : '#666'} angle={-45} textAnchor="end" height={100} />
                                <YAxis stroke={isDarkMode ? 'var(--text-secondary)' : '#666'} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" fill="#468230" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Retention Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <MetricCard
                        icon={<People />}
                        title="New Users (30d)"
                        value={formatNumber(metrics.retention_metrics?.new_users_30d)}
                        subtitle={`${formatNumber(metrics.retention_metrics?.returning_users)} returning users`}
                        color="#6c3082"
                        trend={`${formatPercentage(metrics.retention_metrics?.day_1_retention_rate)} Day 1 Retention`}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper 
                        elevation={0}
                        className="dashboard-card"
                        sx={{
                            p: 3,
                            height: '100%',
                            background: 'transparent',
                            border: `1px solid ${isDarkMode ? 'var(--glass-border)' : 'rgba(108, 48, 130, 0.1)'}`,
                            borderRadius: 'var(--radius-lg)',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 2, color: isDarkMode ? 'var(--text-primary)' : '#2c3e50', fontWeight: 600 }}>
                            Active Users Breakdown
                        </Typography>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={activeUsersData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {activeUsersData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Refresh Button */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <button 
                        className="btn btn-modern-primary btn-lg"
                        onClick={fetchMetrics}
                    >
                        🔄 Refresh Data
                    </button>
            </Box>
        </Box>
    );
}

export default AnalyticsDashboard;
